<script lang="ts" setup>
import { computed, useSlots, type VNodeChild } from 'vue';
import { IconClose } from '@/icons';
import { hasSlotContent } from '../../utils';
import { UiButton } from '../ui-button';
import { UiButtonIcon } from '../ui-button-icon';
interface Props {
  active?: boolean;
  title?: string;
  size?: 'small' | 'medium' | 'large';
  heightOverride?: string;
  closeButton?: boolean;
  zIndex?: number;
  asideTitleTopInset?: string;
  asideTitleRightInset?: string;
  cancelLabel?: string;
  applyLabel?: string;
  applyDisabled?: boolean;
  asidePrimaryLabel?: string;
  asidePrimaryDisabled?: boolean;
  asideSecondaryLabel?: string;
}
const props = withDefaults(defineProps<Props>(), {
  active: false,
  size: 'medium',
  closeButton: true,
  asideTitleTopInset: '0px',
  asideTitleRightInset: '0px',
  applyDisabled: true,
  asidePrimaryDisabled: true,
});
const emit = defineEmits<{
  'update:active': [value: boolean];
  close: [];
  cancel: [];
  apply: [];
  asidePrimary: [];
  asideSecondary: [];
}>();
defineSlots<{
  default?: () => VNodeChild;
  title?: () => VNodeChild;
  aside?: () => VNodeChild;
  asideHeaderActions?: () => VNodeChild;
  'aside-header-actions'?: () => VNodeChild;
}>();
const $slots = useSlots();
const hasAside = computed(() => hasSlotContent($slots.aside));
const hasTitle = computed(() => props.title || hasSlotContent($slots.title));
const hasFooter = computed(() => props.cancelLabel || props.applyLabel);
const hasAsideFooter = computed(() => props.asidePrimaryLabel || props.asideSecondaryLabel);
const hasAsideHeaderActions = computed(
  () => hasSlotContent($slots.asideHeaderActions) || hasSlotContent($slots['aside-header-actions']),
);
const hasAsideHeader = computed(() => hasTitle.value || props.closeButton);
function close() {
  emit('update:active', false);
  emit('close');
}
function onOverlayClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    close();
  }
}
const asideStyle = computed(() => ({
  '--ui-modal-aside-title-top-inset': props.asideTitleTopInset,
  '--ui-modal-aside-title-right-inset': props.asideTitleRightInset,
}));
const containerStyle = computed(() =>
  props.heightOverride
    ? {
        height: props.heightOverride,
        maxHeight: '100%',
      }
    : undefined,
);
</script>

<template>
  <Teleport to="body">
    <Transition
      :enter-from-class="$style.enterFrom"
      :enter-active-class="$style.enterActive"
      :leave-active-class="$style.leaveActive"
      :leave-to-class="$style.leaveTo"
    >
      <div
        v-if="props.active"
        :class="$style.overlay"
        role="dialog"
        aria-modal="true"
        data-ui-component="UiModal"
        :style="props.zIndex != null ? { zIndex: props.zIndex } : undefined"
        @click="onOverlayClick"
      >
        <div :class="[$style.container, $style[props.size]]" :style="containerStyle">
          <div :class="$style.body">
            <div :class="$style.main">
              <div v-if="!hasAside && hasTitle" :class="$style.header">
                <h3 :class="$style.title">
                  <slot name="title">{{ props.title }}</slot>
                </h3>
                <UiButtonIcon
                  v-if="props.closeButton"
                  variant="tertiary"
                  size="l"
                  aria-label="Close"
                  @click="close"
                >
                  <IconClose />
                </UiButtonIcon>
              </div>
              <slot />
            </div>

            <div v-if="hasAside" :class="$style.aside" :style="asideStyle">
              <div
                v-if="hasAsideHeaderActions && !hasAsideHeader"
                :class="$style.asideFloatingHeaderActions"
              >
                <slot name="aside-header-actions" />
              </div>
              <div
                v-if="hasAsideHeader"
                :class="$style.asideHeader"
              >
                <h3 v-if="hasTitle" :class="[$style.title, $style.asideTitle]">
                  <slot name="title">{{ props.title }}</slot>
                </h3>
                <div v-if="hasAsideHeaderActions" :class="$style.asideHeaderActions">
                  <slot name="aside-header-actions" />
                </div>
                <UiButtonIcon
                  v-else-if="props.closeButton"
                  variant="tertiary"
                  size="l"
                  aria-label="Close"
                  @click="close"
                >
                  <IconClose />
                </UiButtonIcon>
              </div>

              <div :class="[$style.asideContent, !hasAsideHeader && $style.asideContentPaddedTop]">
                <slot name="aside" />
              </div>

              <div v-if="hasAsideFooter" :class="$style.asideFooter">
                <UiButton
                  v-if="props.asidePrimaryLabel"
                  size="l"
                  block
                  :disabled="props.asidePrimaryDisabled"
                  @click="emit('asidePrimary')"
                >
                  {{ props.asidePrimaryLabel }}
                </UiButton>
                <UiButton
                  v-if="props.asideSecondaryLabel"
                  variant="secondary"
                  size="l"
                  block
                  @click="emit('asideSecondary')"
                >
                  {{ props.asideSecondaryLabel }}
                </UiButton>
              </div>
            </div>
          </div>

          <div v-if="hasFooter" :class="$style.footer">
            <UiButton v-if="props.cancelLabel" variant="secondary" size="l" @click="emit('cancel')">
              {{ props.cancelLabel }}
            </UiButton>
            <UiButton
              v-if="props.applyLabel"
              size="l"
              :disabled="props.applyDisabled"
              @click="emit('apply')"
            >
              {{ props.applyLabel }}
            </UiButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/typography';

.overlay {
  position: fixed;
  inset: 0;
  display: grid;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: var(--ui2-shade-black-32);
  z-index: 1000;
}

.container {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--ui2-background-primary, #fff);
  border-radius: 12px;
  overflow: hidden;
  box-sizing: border-box;
  font-family: typography.$font-family-primary;
}

.small {
  width: 480px;
  max-width: 100%;
  max-height: 100%;
}

.medium {
  width: 900px;
  height: 560px;
  max-width: 100%;
  max-height: 100%;
}

.large {
  width: calc(100vw - 120px);
  height: 800px;
  max-height: 100%;
}

.body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  overflow-y: auto;
  min-width: 0;
}

.header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.title {
  flex: 1;
  min-width: 0;
  margin: 0;
  @include typography.style('heading-h3');
  color: var(--ui2-content-primary, #010000);
}

.aside {
  position: relative;
  flex: none;
  width: 340px;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--ui2-stroke-tertiary, #f0f1f4);
}

.asideHeader {
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  flex: none;
}

.asideTitle {
  padding-top: var(--ui-modal-aside-title-top-inset, 0px);
  padding-right: var(--ui-modal-aside-title-right-inset, 0px);
}

.asideHeaderActions {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.asideFloatingHeaderActions {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1;
}

.asideContent {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px;
  min-height: 0;
}

.asideContentPaddedTop {
  padding-top: 20px;
}

.asideFooter {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 20px 20px;
}

.footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 8px 20px 20px;
  background: var(--ui2-background-primary, #fff);
}

.enterActive,
.leaveActive {
  transition: opacity 0.2s ease;
}

.enterFrom,
.leaveTo {
  opacity: 0;
}
</style>
